import { X, Archive } from "lucide-react";
import type { Distributor, ModalBaseProps } from "./types";

interface BulkArchiveDistributorModalProps extends ModalBaseProps {
  distributors: Distributor[];
  loading: boolean;
  onConfirm: () => void;
}

export function BulkArchiveDistributorModal({
  isOpen,
  onClose,
  distributors,
  loading,
  onConfirm,
}: BulkArchiveDistributorModalProps) {
  if (!isOpen || distributors.length === 0) return null;

  const handleClose = () => {
    onClose();
  };

  return (
    <div className="fixed inset-0 flex items-center justify-center z-50 p-4 bg-black/30 backdrop-blur-sm">
      <div
        className="bg-card rounded-lg shadow-2xl max-w-lg w-full border divide-y border-border divide-border max-h-[90vh] flex flex-col overflow-hidden"
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="bulk-archive-distributor-title"
      >
        <div className="flex justify-between items-center p-8">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-orange-100 dark:bg-orange-900/30">
              <Archive className="h-5 w-5 text-orange-600 dark:text-orange-400" />
            </div>
            <div>
              <h2
                id="bulk-archive-distributor-title"
                className="text-xl font-semibold"
              >
                Archive Multiple Distributors
              </h2>
              <p className="text-xs text-muted-foreground mt-1">
                This action can be reversed.
              </p>
            </div>
          </div>
          <button
            onClick={handleClose}
            className="hover:opacity-70 transition-opacity"
            aria-label="Close dialog"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="p-8 space-y-6 flex-1 overflow-y-auto min-h-0">
          <p>
            Are you sure you want to archive <strong>{distributors.length}</strong>{" "}
            distributor{distributors.length > 1 ? "s" : ""}? Archived distributors cannot be selected in redemption requests.
          </p>

          <div className="space-y-1">
            {distributors.map((distributor) => (
              <div
                key={distributor.id}
                className="text-sm px-3 py-2 rounded bg-muted text-foreground"
              >
                <strong>{distributor.name}</strong>
              </div>
            ))}
          </div>
        </div>

        {/* Footer */}
        <div className="p-8 flex justify-end gap-3">
          <button
            onClick={handleClose}
            className="px-6 py-3 rounded-lg font-semibold transition-colors bg-muted hover:bg-accent text-foreground border border-border"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={loading}
            className="px-6 py-3 rounded-lg font-semibold transition-colors bg-orange-600 hover:bg-orange-700 text-white disabled:opacity-50"
          >
            {loading
              ? "Archiving..."
              : `Archive ${distributors.length} Distributor${
                  distributors.length > 1 ? "s" : ""
                }`}
          </button>
        </div>
      </div>
    </div>
  );
}
